import { useState, useCallback } from 'react';

import { Check, Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConnectorIcon } from '@/components/connectors/ConnectorIcon';

import { Connection, ConnectorListItem } from '@optidev/shared-types';

interface ConnectionUsageInstructionsModalProps {
  connection: Connection | null;
  connector?: ConnectorListItem | null;
  isOpen: boolean;
  onClose: () => void;
}

/** The account email some connectors append to the name is noise here. */
const stripAccount = (name: string): string =>
  name.replace(/\(([^)]+@[^)]+)\)/, '').replace(/\s+/g, ' ').trim();

/**
 * How to use a connection, for someone who will never write the wiring themselves.
 *
 * This used to render per-connector code samples fetched from the API — env var names, SDK
 * snippets, language tabs. The agent writes all of that now. The only thing left for a person to
 * do is name the connection in chat, so the modal is one sentence to copy, with the ID already
 * in it and nothing after it — nothing to assemble and nothing to choose between.
 */
export function ConnectionUsageInstructionsModal({
  connection,
  connector,
  isOpen,
  onClose,
}: ConnectionUsageInstructionsModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  if (!connection) return null;

  const serviceName = connector?.name ?? connection.type;
  const connectionName = stripAccount(connection.name);
  const prompt = `Use my ${serviceName} connection (ID: ${connection.id})`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="grid size-9 flex-none place-items-center rounded-xl bg-white p-1.5 shadow-sm ring-1 ring-border">
              <ConnectorIcon
                connector={connector ?? { icon: 'Database', name: serviceName }}
                className="size-full"
                // Matches the card's white tile, so the mark stays dark in both themes.
                surface="light"
              />
            </span>
            <div className="min-w-0">
              <DialogTitle className="truncate text-base">Using {connectionName}</DialogTitle>
              <DialogDescription className="text-xs">
                Paste this into the agent chat, then say what you want built.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-2.5">
          <p className="select-all rounded-xl border border-border bg-muted/40 p-3.5 text-[13px] leading-relaxed">
            Use my {serviceName} connection (ID:{' '}
            <code className="font-mono text-[12px]">{connection.id}</code>)
          </p>

          <Button type="button" onClick={() => handleCopy(prompt)} className="w-full gap-2">
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            {copied ? 'Copied to clipboard' : 'Copy'}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            You never have to paste a key or password into the chat.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ConnectionUsageInstructionsModal;
